import { fromBase64 } from '@cosmjs/encoding'
import { makeAuthInfoBytes, makeSignDoc } from '@cosmjs/proto-signing'
import { useChain } from '@cosmos-kit/react'
import { useQuery } from '@tanstack/react-query'
import { SignMode } from 'cosmjs-types/cosmos/tx/signing/v1beta1/signing'
import { TxBody, TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { Any } from 'cosmjs-types/google/protobuf/any'
import { WalletStatus } from 'cosmos-kit'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { fetchAccount, fetchMultisigGroups, fetchNftModuleParams } from '@/api'
import type { NftModuleParams } from '@/api/modules/nft'
import { bridgelessChainInfo, CHAIN_NAME } from '@/config'
import { ErrorHandler } from '@/helpers'
import {
  getBalance,
  getKelprClient,
  getKeplr,
  suggestAndEnableKeplr,
} from '@/libs/keplr'
import { queryKeys } from '@/queries/keys'

type KeplrWalletContextValue = {
  status: WalletStatus
  isWalletConnected: boolean

  username: string | null
  address: string | null

  balance: string | null
  isBalanceLoading: boolean

  connect: () => Promise<void>
  disconnect: () => void

  nftModuleParams: NftModuleParams | null
  isModuleAdmin: boolean
  isMultisigMember: boolean
  isMultisigMemberLoading: boolean

  signAndBroadcast: (msgs: Any[]) => Promise<string>
}

const KeplrWalletContext = createContext<KeplrWalletContextValue | undefined>(
  undefined,
)

export function KeplrWalletProvider({ children }: PropsWithChildren) {
  const { status, address, connect, disconnect, username, isWalletConnected } =
    useChain(CHAIN_NAME)

  const { data: keplrClient } = useQuery({
    queryKey: queryKeys.wallet.keplrClient(bridgelessChainInfo.rpc),
    queryFn: async () => getKelprClient(bridgelessChainInfo.rpc),
    staleTime: Infinity,
    enabled: isWalletConnected,
  })

  const connectWithSuggest = useCallback(async () => {
    try {
      if (getKeplr()) {
        await suggestAndEnableKeplr(bridgelessChainInfo)
      }
    } catch (e) {
      ErrorHandler.process(e, 'Failed to suggest bridgeless chain to Keplr')
    }
    await connect()
  }, [connect])

  // Module admin query (centralized here per request)
  const { data: nftModuleParams } = useQuery({
    queryKey: queryKeys.wallet.nftModuleParams(),
    queryFn: fetchNftModuleParams,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  // Multisig membership query
  const { data: multisigData, isLoading: isMultisigDataLoading } = useQuery({
    queryKey: queryKeys.admin.multisigGroups(),
    queryFn: () => fetchMultisigGroups(),
    enabled: Boolean(isWalletConnected && address),
    staleTime: 60_000,
  })

  // Poll balance every 5s globally when connected
  const { data: polledBalance, isLoading: isBalanceLoading } = useQuery({
    queryKey: queryKeys.wallet.keplrBalance(
      address,
      bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
      bridgelessChainInfo.rpc,
    ),
    queryFn: async () => {
      if (!address || !keplrClient) return null
      return getBalance(
        keplrClient,
        address,
        bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
      )
    },
    enabled: Boolean(address && keplrClient),
    refetchInterval: 5000,
    staleTime: 3000,
  })

  const signAndBroadcast = useCallback(
    async (msgs: Any[]) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      const keplr = getKeplr()
      if (!keplr) {
        throw new Error('Keplr not found')
      }

      if (!keplrClient) {
        throw new Error('Keplr client not initialized')
      }

      await keplr.enable(bridgelessChainInfo.chainId)
      const signer = keplr.getOfflineSigner(bridgelessChainInfo.chainId)
      const [account] = await signer.getAccounts()

      // 1️) account number / sequence
      const {
        account: { base_account },
      } = await fetchAccount(account.address)

      const accountNumber = BigInt(base_account.account_number)
      const sequence = BigInt(base_account.sequence)

      // 2) TxBody
      const txBodyBytes = TxBody.encode({
        messages: msgs,
        memo: '',
        timeoutHeight: 0n,
        extensionOptions: [],
        nonCriticalExtensionOptions: [],
      }).finish()

      // 3) AuthInfo
      const pubkey = {
        typeUrl: '/ethermint.crypto.v1.ethsecp256k1.PubKey',
        value: Uint8Array.from([
          0x0a,
          account.pubkey.length,
          ...account.pubkey,
        ]),
      }

      const GAS = 10_000_000
      const MIN_GAS_PRICE = 1_000_000_000

      const feeAmount = [
        {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: String(GAS * MIN_GAS_PRICE),
        },
      ]

      const authInfoBytes = makeAuthInfoBytes(
        [{ pubkey, sequence }],
        feeAmount,
        GAS,
        undefined,
        undefined,
        SignMode.SIGN_MODE_DIRECT,
      )

      // 4) Sign
      const signDoc = makeSignDoc(
        txBodyBytes,
        authInfoBytes,
        bridgelessChainInfo.chainId,
        Number(accountNumber),
      )

      const { signed, signature } = await signer.signDirect(
        account.address,
        signDoc,
      )

      // 5) Broadcast
      const txRaw = TxRaw.fromPartial({
        bodyBytes: signed.bodyBytes,
        authInfoBytes: signed.authInfoBytes,
        signatures: [fromBase64(signature.signature)],
      })

      const result = await keplrClient.broadcastTx(TxRaw.encode(txRaw).finish())

      if (result.code !== 0) {
        throw new Error(result.rawLog || 'Broadcast failed')
      }

      return result.transactionHash
    },
    [address, keplrClient],
  )

  const value = useMemo<KeplrWalletContextValue>(
    () => ({
      status,
      address: address ?? null,
      balance: polledBalance ?? null,
      isBalanceLoading,
      connect: connectWithSuggest,
      disconnect,
      username: username ?? null,
      isWalletConnected,
      nftModuleParams: nftModuleParams ?? null,
      isModuleAdmin: Boolean(
        address && nftModuleParams?.module_admin === address,
      ),
      isMultisigMember: Boolean(
        address &&
        multisigData?.group?.some(group =>
          group.members.some(
            member => member.toLowerCase() === address.toLowerCase(),
          ),
        ),
      ),
      isMultisigMemberLoading: isMultisigDataLoading,
      signAndBroadcast,
    }),
    [
      status,
      address,
      polledBalance,
      isBalanceLoading,
      connectWithSuggest,
      disconnect,
      username,
      isWalletConnected,
      nftModuleParams,
      multisigData,
      isMultisigDataLoading,
      signAndBroadcast,
    ],
  )

  return (
    <KeplrWalletContext.Provider value={value}>
      {children}
    </KeplrWalletContext.Provider>
  )
}

export function useKeplrWallet() {
  const ctx = useContext(KeplrWalletContext)
  if (!ctx)
    throw new Error('useKeplrWallet must be used within KeplrWalletProvider')
  return ctx
}
